module Client.Admin.EditUser.EditTags

open Client
open Shared
open System
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared.Admin.Tags.StronglyTypedIds
open Shared.Admin.EditTags

type Model = 
    {
        UserId: UserId
        Tags: TagDTO list 
        SelectedTags: TagId Set
        Loading: bool 
        Saving: bool
    }

let toDTO (m: Model) = m.SelectedTags

type Event = 
    | ApiError of ApiError
    | SaveSuccessful of notification: string 
    | CloseOverlay

type Msg =
    | InitDataLoaded of InitData
    | ApiError of ApiError
    | SelectTag of TagId
    | UnselectTag of TagId
    | SaveClicked
    | SaveResult of FailureReason option
    | CloseOverlay

let init userId = 
    {
        UserId = userId
        Tags = []
        SelectedTags = [] |> Set
        Loading = true
        Saving = false
    }, Api.secureCall<IEditTagsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getInitData) userId InitDataLoaded Msg.ApiError

let update msg model = 
    match msg with 
    | Msg.ApiError err -> model, err |> Event.ApiError |> SideEffect.Event 
    | Msg.CloseOverlay -> model, Event.CloseOverlay |> SideEffect.Event 
    | InitDataLoaded d -> 
        {model with 
            Tags = d.Tags
            SelectedTags = d.DTO
            Loading = false
        }, SideEffect.none
    | SelectTag t -> 
        {model with SelectedTags = Set.add t model.SelectedTags}, SideEffect.none
    | UnselectTag t -> 
        {model with SelectedTags = Set.remove t model.SelectedTags}, SideEffect.none
    | SaveClicked -> 
        {model with Saving = true},
        Api.secureCall<IEditTagsApi,_,_,_> SecuredRoute.Admin (fun api -> api.edit model.UserId) (toDTO model) SaveResult Msg.ApiError
        |> SideEffect.Cmd
    | SaveResult reason -> 
        match reason with 
        | None 
        | Some NothingToDo -> model, "User Tags were updated successfully." |> SaveSuccessful |> SideEffect.Event

let renderTag loading selected dispatch (t: TagDTO) = 
    a [
        ClassName (jQueryUI.addSkeletonScreen "emblem" None loading)
        OnClick 
            (fun _ -> 
                if not loading then
                    if selected then UnselectTag (TagId t.Id) else SelectTag (TagId t.Id)
                    |> dispatch
            )
    ] [
        str t.Name
    ]

let skeletonTag() = 
    {
        Id = Guid.NewGuid()
        Name = "Tag Name"
    }

let skeletonScreen = 
    [
        skeletonTag()
        skeletonTag()
        skeletonTag()
    ]

let view model dispatch = 
    let selected, unselected = model.Tags |> List.partition (fun t -> Set.contains (TagId t.Id) model.SelectedTags)
    jQueryUI.modal "Edit User Tags" 50 Msg.CloseOverlay dispatch (Some 800) [
        div [
            Id "edit-users-tags-container"
        ] [
            jQueryUI.inputGroup [
                label [] [
                    strong [] [ str "Available Tags" ]
                ]
                div [
                    ClassName "emblems-container"
                ] [
                    yield! (if model.Loading then skeletonScreen else unselected) 
                        |> List.map (renderTag model.Loading false dispatch)
                ]
            ]
            
            jQueryUI.inputGroup [
                label [] [
                    strong [] [ str "Selected Tags" ]
                ]
                div [
                    ClassName "emblems-container"
                ] [
                    if not model.Loading && List.isEmpty selected then
                        p [] [
                            str "No tags are currently selected."
                        ]
                    else
                        yield! (if model.Loading then skeletonScreen else selected) 
                            |> List.map (renderTag model.Loading true dispatch)
                ]
            ]
            
        ]
        yield! UI.saveControls (Some "Update User Tags") false model.Saving SaveClicked Msg.CloseOverlay dispatch
    ]